<template>
    <div class="tw-flex-1" :class="[{ 'with-color-tag': color_tag }, { 'has-value': currentValue != null }]">
        <validation-provider class="tw-flex tw-flex-col tw-flex-1 tw-relative"
            :class="[{ 'multiple-form-select': multiple }, { 'inside-modal': inside_modal }, { 'color-tags-select': color_tag }]"
            :rules="rules" :name="name" :vid="vid" v-slot="{ errors, classes }" :mode="custom_validation">
            <label class="form-label" :for="name" v-text="label" v-if="label"></label>
            <template v-if="multiple">
                <v-select multiple :components="{ OpenIndicator }"
                    :placeholder="placeholder === '' ? translations['fields.click_to_select'] : placeholder"
                    :closeOnSelect="false" transition="" :options="allOptions" :reduce="option => option[name_clean]"
                    v-model="currentValue" :clearable="clearable" :searchable="searchable" :label="display_name"
                    :taggable="taggable" @option:created="emitOptionCreate" @input="change">
                    <template v-slot:list-header v-if="show_header">
                        <slot name="header"></slot>
                    </template>
                    <template v-slot:no-options v-if="show_custom_error_on_empty">
                        <div class="tw-flex tw-flex-col tw-items-center tw-mb-2" v-if="no_data_message !== ''">
                            <p class="tw-text-gray-600 tw-mb-2" v-text="no_data_message"></p>
                            <div class="tw-inline-flex">
                                <div class="general-button slim tw-text-sm" @mousedown="emitClickedOnEmpty">
                                    <i class="far fa-plus"></i>
                                    <p v-text="no_data_btn_message"></p>
                                </div>
                            </div>
                        </div>
                        <slot v-else name="empty">
                            <template>
                                <span v-text="translations['form.no_matching_options']"></span>
                            </template>
                        </slot>
                    </template>
                </v-select>
            </template>
            <template v-else>
                <v-select :components="{ OpenIndicator }"
                    :placeholder="placeholder === '' ? translations['fields.click_to_select'] : placeholder"
                    transition="" :options="allOptions" :reduce="option => option[name_clean]" v-model="currentValue"
                    :clearable="clearable" :searchable="searchable" :label="display_name" :taggable="taggable"
                    @option:created="emitOptionCreate" @input="change" ref="vSelect">
                    <template v-slot:list-header v-if="show_header">
                        <slot name="header"></slot>
                    </template>
                    <template v-slot:option="category" v-if="color_tag">
                        <span class="pill hover:tw-opacity-75 tw-text-white"
                            :style="{ backgroundColor: category.color }">{{ category.name }} ({{ category.events_count
                            }})</span>
                    </template>
                    <template #selected-option="category" v-if="color_tag">
                        <span class="pill hover:tw-opacity-75 tw-text-white"
                            :style="{ backgroundColor: category.color }">{{ category.name }} ({{ category.events_count
                            }})</span>
                    </template>
                    <template v-slot:no-options v-if="show_custom_error_on_empty">
                        <div class="tw-flex tw-flex-col tw-items-center tw-mb-2" v-if="no_data_message !== ''">
                            <p class="tw-text-gray-600 tw-mb-2" v-text="no_data_message"></p>
                            <div class="tw-inline-flex">
                                <div class="general-button slim tw-text-sm" @mousedown="emitClickedOnEmpty">
                                    <i class="far fa-plus"></i>
                                    <p v-text="no_data_btn_message"></p>
                                </div>
                            </div>
                        </div>
                        <slot v-else name="empty">
                            <template>
                                <span v-text="translations['form.no_matching_options']"></span>
                            </template>
                        </slot>
                    </template>
                </v-select>
            </template>
            <span class="form-validation-error" v-if="errors.length > 0 && display_errors">{{ errors[0] }}.</span>
            <p class="helper" v-if="helper" v-text="helper"></p>
        </validation-provider>
        <div class="tw-inline-flex tw-mt-3" @click="emitRefresh" v-if="allow_refresh">
            <div class="ghost-button tw-text-sm slim">
                <i class="fal fa-sync"></i>
                <p v-text="refresh_message"></p>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import FormInput from '../../mixins/FormInput';
import Validation from '../../mixins/v2/Validation';
import Global from '../../mixins/Global';

export default {
    name: 'FormSelect',

    mixins: [Global, FormInput, Validation],

    components: {
        'v-select': vSelect,
    },

    props: {
        options: {
            type: Array,
            required: true,
        },

        name_clean: {
            required: true,
        },

        display_name: {
            type: String,
            required: true,
        },

        clearable: {
            type: Boolean,
            default: false,
        },

        searchable: {
            type: Boolean,
            default: false,
        },

        multiple: {
            type: Boolean,
            default: false,
        },

        taggable: {
            type: Boolean,
            default: false,
        },

        no_data_message: {
            type: String,
            default: '',
        },

        no_data_btn_message: {
            type: String,
            default: '',
        },

        refresh_message: {
            type: String,
            default: '',
        },

        show_custom_error_on_empty: {
            type: Boolean,
            default: true,
        },

        show_header: {
            default: false,
        },

        allow_empty: {
            default: false,
        },

        empty_value: {
            default: null,
        },
        color_tag: {
            type: Boolean,
            default: false,
        },
        inside_modal: {
            default: false,
        },
        isGroup: {
            default: false,
        }
    },

    data() {
        return {
            display_errors: true,
            allow_refresh: false,
            OpenIndicator: {
                render: createElement => createElement('i', {
                    class: {
                        toggle: true,
                        fal: true,
                        'mt-1': true,
                        'fa-angle-down': true,
                    },
                }),
            },
        };
    },

    computed: {
        allOptions() {
            const { options } = this;

            if (this.allow_empty) {
                options.unshift({ [this.name_clean]: this.empty_value, [this.display_name]: 'None' });
            }

            return options;
        },
    },

    methods: {
        custom_validation() {
            return { on: ['search:blur', 'input'] };
        },

        emitOptionCreate(option) {
            this.display_errors = false;
            // this.$emit('option:created', option[this.display_name]);
            this.$emit('option:created', option);

            // dont display errors while creating new option
            setTimeout(() => {
                this.display_errors = true;
            }, 2500);
        },

        emitClickedOnEmpty() {
            this.$emit('clickedOnEmpty');
            this.allow_refresh = true;
        },

        emitRefresh() {
            this.$emit('refresh');
            this.allow_refresh = false;
        },

        change() {
            this.$nextTick(() => {
                this.$emit('change');
            });
        },
    },
};
</script>

<style lang="scss">
@import '~vue-select/src/scss/vue-select.scss';

.with-color-tag {
    .vs__dropdown-menu {
        @apply tw-pt-5 tw-flex tw-flex-wrap tw-px-3 #{!important};
    }

    .vs__dropdown-option {
        @apply tw-p-0 tw-mb-4;

        &.vs__dropdown-option--selected {
            @apply tw-pl-0 #{!important};

            &:before {
                display: none;
            }

            .pill {
                @apply tw-pl-7;

                &:before {
                    @apply tw-absolute;
                    font-family: 'Font Awesome 5 Pro';
                    content: "\f00c";
                    color: #fff;
                    left: 9px;
                    top: 4px;
                }
            }
        }
    }

    &.has-value {
        .vs__dropdown-toggle {
            @apply tw-py-0;
            height: 44px;
        }

        .vs__selected-options {
            @apply tw-items-center;
        }
    }
}

.v-select {
    @apply tw-z-20;
    filter: drop-shadow(0px 0px 2px #00000029);
}

.vs__dropdown-menu {
    @apply tw-bg-white tw-border-none tw-p-0 tw-shadow-none tw-pb-1 tw-mb-4;

    @media(max-width: 1130px) {
        min-width: 0;
    }
}

.vs__dropdown-option {
    @apply tw-py-3 tw-px-2 tw-text-gray-600 tw-mx-1 tw-rounded;

    &.vs__dropdown-option--selected {
        @apply tw-pl-7;

        &:before {
            @apply tw-absolute tw-text-gray-600 tw-font-extralight;
            font-family: 'Font Awesome 5 Pro';
            content: "\f00c";
            left: 12px;
        }
    }
}

.vs__dropdown-option--highlight {
    @apply tw-bg-gray-0;
}

.vs__clear,
.vs__open-indicator {
    fill: #394066;
}

.vs__dropdown-toggle {
    @apply tw-border-none tw-rounded-lg tw-px-4 tw-bg-white;
    padding-top: 0.9rem;
    padding-bottom: 0.9rem;

    &:hover {
        filter: brightness(0.98);
    }
}

.vs__open-indicator {
    fill: #9e9e9e;
    // transform: scale(0.6);
    width: 23px;
    height: 19px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.vs__clear {
    fill: #9e9e9e;
    transform: scale(0.7);
}

.vs--open {
    @apply tw-z-30;
    // filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.07));

    .vs__dropdown-toggle {
        @apply tw-bg-white;
    }

    .vs__open-indicator {
        transform: rotate(180deg);
    }
}

.vs__selected,
.vs__search {
    @apply tw-m-0 tw-p-0 tw-border-none tw-text-gray-600 #{!important};
}

.vs__search {
    &::placeholder {
        @apply tw-text-gray-400;
        // Commented this out else the text is not on one line
        // transform: translateY(2px);
    }
}

.vs__actions {
    padding: 0;
}

.multiple-form-select {
    .v-select {
        .vs__dropdown-toggle {
            @apply tw-pt-2 #{!important};
        }

        .vs__selected {
            @apply tw-bg-white tw-px-2 tw-py-1 tw-mr-2 tw-mt-1 tw-relative #{!important};
        }

        .vs__deselect {
            width: 12px;

            &:before {
                @apply tw-absolute tw-text-gray-300 tw-font-extralight;
                content: "\f00d";
                font-family: 'Font Awesome 5 Pro';
                top: 5px;
                right: 10px;
            }

            svg {
                @apply tw-hidden;
            }
        }

        &.vs--open {
            .vs__selected {
                @apply tw-bg-gray-0 #{!important};
            }
        }
    }
}

.vs__no-options {
    @apply tw-text-gray-400 tw-pb-2 tw--mt-3;
    margin-left: 0.4rem;
}

.inside-modal {
    .v-select {
        filter: drop-shadow(0px 0px 0px #00000029);
    }

    .vs__dropdown-toggle {
        @apply tw-bg-input;

        &:hover {
            filter: none;
            background-color: #eaecef;
        }
    }

    .vs--open {
        filter: drop-shadow(0 2px 2px #00000029);

        .vs__dropdown-toggle {
            background-color: #eaecef;
        }
    }
}

.color-tags-select {
    .color-tag {
        height: 30px;
        min-width: 6px;
        border-radius: 4px 0px 0px 4px;
    }

    .vs__dropdown-option {
        &.vs__dropdown-option--selected {
            &:before {
                @apply tw-pt-2;
            }

            .color-tag {
                @apply tw-ml-2;
            }
        }
    }


}
</style>